import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { useLuckyDrawHistoryDetail, useStats } from "@/lib/hooks/useApi";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import AnimatedLoading from "@/components/AnimatedLoading";
import { AvatarBorder } from "@/components/AvatarBorder";
import { UserName } from "@/components/UserName";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import dayjs from "dayjs";
import { t } from "@/lib/i18n";

export const Route = createFileRoute("/(game)/lucky-draw/history/$drawId")({
  component: DrawDetailComponent,
});

function DrawDetailComponent() {
  const { drawId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;

  const { data, isLoading } = useLuckyDrawHistoryDetail({
    id: drawId,
    userId: statsData?.userId,
  });
  const prizes = data?.prizes ?? [];
  const luckyDraw = data?.luckyDraw;
  const ticketEntries = data?.ticketEntries ?? [];
  const winningTicketSet = useMemo(() => {
    if (!gameWalletAddress) return new Set<string>();
    const codes = prizes
      .map((prize) => prize.winner)
      .filter(
        (winner) =>
          winner?.address === gameWalletAddress && Boolean(winner.ticketCode),
      )
      .map((winner) => winner!.ticketCode as string);
    return new Set(codes);
  }, [prizes, gameWalletAddress]);
  const orderedEntries = useMemo(() => {
    if (winningTicketSet.size === 0) return ticketEntries;
    const winningEntries = ticketEntries.filter((entry) =>
      winningTicketSet.has(entry.ticketCode),
    );
    const remainingEntries = ticketEntries.filter(
      (entry) => !winningTicketSet.has(entry.ticketCode),
    );
    return [...winningEntries, ...remainingEntries];
  }, [ticketEntries, winningTicketSet]);
  const entryOrderMap = useMemo(
    () => new Map(ticketEntries.map((entry, index) => [entry.id, index + 1])),
    [ticketEntries],
  );

  if (isLoading || !luckyDraw) {
    return <AnimatedLoading />;
  }
  const isLive = luckyDraw.status === "live";
  const isDistributed = luckyDraw.status === "distributed";
  const sortedPrizes = [...prizes].sort((a, b) => a.prizeOrder - b.prizeOrder);

  return (
    <div className="relative flex flex-1 min-h-0 flex-col gap-4 bg-[#1D2A52]">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/lucky-draw/history" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <div className="mt-16 px-2 flex flex-col w-full items-center justify-center gap-1">
        <h2 className="text-3xl font-bold text-white uppercase text-center text-wrap">
          {t("luckyDraw.drawDetailsTitle")}
        </h2>
      </div>
      <div
        className={cn(
          "space-y-3 bg-[#1D2A52] px-3 pb-3",
          "border-t-4 border-[#0659B5]",
          "flex-1 min-h-0 overflow-y-auto py-3",
        )}
      >
        <div className="dark-blue-container p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs uppercase tracking-wider">
              {t("luckyDraw.statusLabel")}
            </span>
            <span
              className={cn(
                "text-xs font-semibold uppercase px-2 py-0.5 rounded",
                isDistributed
                  ? "bg-green-500/20 text-green-400"
                  : isLive
                    ? "bg-blue-500/20 text-blue-400"
                    : "bg-white/10 text-white/60",
              )}
            >
              {luckyDraw.status}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs uppercase tracking-wider">
              {t("luckyDraw.totalPrizeLabel")}
            </span>
            <span className="text-white font-semibold text-sm">
              ${luckyDraw.totalPrizeInUsd?.toLocaleString() ?? "0"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs uppercase tracking-wider">
              {t("luckyDraw.startLabel")}
            </span>
            <span className="text-white/80 text-sm">
              {dayjs
                .unix(luckyDraw.startDate)
                .format(t("luckyDraw.drawDateTimeFormat"))}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs uppercase tracking-wider">
              {t("luckyDraw.endLabel")}
            </span>
            <span className="text-white/80 text-sm">
              {dayjs
                .unix(luckyDraw.endDate)
                .format(t("luckyDraw.drawDateTimeFormat"))}
            </span>
          </div>
        </div>

        <div className="dark-blue-container p-4 flex flex-col gap-3 max-h-72 overflow-y-hidden">
          {sortedPrizes.length > 0 && (
            <h2 className="text-white font-semibold text-sm uppercase tracking-wider">
              {sortedPrizes.length === 1
                ? t("luckyDraw.winnerLabel")
                : t("luckyDraw.winnersLabel")}
            </h2>
          )}
          {sortedPrizes.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-4">
              {t("luckyDraw.noWinnersAnnounced")}
            </p>
          ) : (
            <div className="flex flex-col gap-2 overflow-y-scroll">
              {sortedPrizes.map((prize) => (
                <div
                  key={prize.prizeOrder}
                  className="flex items-center gap-3 rounded bg-white/5 p-3"
                >
                  <div className="flex-1 min-w-0 flex items-start">
                    {prize.winner ? (
                      <div className="flex justify-center gap-2">
                        <AvatarBorder
                          src={prize.winner.profilePicture || ""}
                          decoration={prize.winner.activeFrameUrl || ""}
                          size={40}
                        />
                        <div className="flex flex-col items-start justify-start">
                          <span className="text-white/40 text-xs font-mono">
                            #{prize.winner.ticketCode}
                          </span>
                          <UserName
                            username={prize.winner.username}
                            className="font-semibold normal-case text-sm"
                            iconSize="w-3 h-3"
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-white/40 text-sm">—</span>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-white font-bold text-sm">
                      {prize.amountUsdc != null ? `$${prize.amountUsdc}` : prize.prizePct != null ? `${prize.prizePct}%` : `Prize #${prize.prizeOrder}`}
                    </span>
                    <span className="text-white/40 text-xs">
                      {t("luckyDraw.prizeLabel", { order: prize.prizeOrder })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dark-blue-container p-4 flex flex-col gap-3 max-h-80 overflow-y-hidden">
          <h2 className="text-white font-semibold text-sm uppercase tracking-wider">
            {t("luckyDraw.yourLuckyTickets")}
          </h2>
          {!walletAddress ? (
            <p className="text-white/40 text-sm text-center py-4">
              {t("luckyDraw.missedDraw")}
            </p>
          ) : ticketEntries.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-4">
              {t("luckyDraw.noTicketsEnteredForDraw")}
            </p>
          ) : (
            <div className="flex flex-col gap-2 overflow-y-scroll">
              {orderedEntries.map((entry, index) => {
                const ticketOrder = entryOrderMap.get(entry.id) ?? index + 1;

                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between rounded bg-white/5 px-3 py-2"
                  >
                    <span className="text-white/70 text-xs uppercase">
                      {t("luckyDraw.luckyTicketNumber", {
                        order: ticketOrder,
                      })}
                    </span>
                    <span
                      className={cn(
                        "font-mono text-sm font-semibold tracking-wider",
                        winningTicketSet.has(entry.ticketCode)
                          ? "text-green-300"
                          : "text-white",
                      )}
                    >
                      #{entry.ticketCode}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
